import _ from 'lodash';
import moment from 'moment';
import { ANNOTATION_TYPE } from '@/shared/ui';
import {
  BEAT_EVENT_LIST,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  typesPersistEventV2,
} from '@/shared/config';
import {
  convertEventNameFromType,
  convertToMomentJs,
  getEventDuration,
} from '@/shared/lib/aiUtils';
import { getTotalTimeWithin } from '@/shared/lib/timeRange';

/**
 * Builds the title and value rows shown in the ECG event detail panel. Switches on the type, resolved through oldType for deleted events, emitting start and end times in the viewer timezone, a duration from the ECG data map or the raw span, channel and min, avg and max heart rate per type, then beat-event rows, a captured-strips row sorted by each capture's sample-mark annotation, and for an untyped minute a fallback of time, channel, heart rate and the captures of nearby events.
 *
 * @param info - The event to describe, plus the other events and minute-info events used for the untyped minute case.
 * @param aiChannel - Channel fallback used when the event carries none.
 * @param timezoneOffset - Offset applied to every rendered timestamp.
 * @param ecgDataMap - Recorded data map the duration of persisted event types is measured against.
 * @returns An array of title and value rows for the detail panel, empty when no event is supplied.
 */
export const formatDisplayEvent = (
  info: any,
  aiChannel: any,
  timezoneOffset: any,
  ecgDataMap: any,
) => {
  const { event } = info || {};
  if (_.isEmpty(event)) return [];

  const { start, stop, startTimeEpoch, captures, oldType, duration } = event || {};
  const type =
    event?.type === TYPE_BEAT_ENUM.DELETED ? event?.oldType : event?.type || event?.oldType;
  const displayData = [];

  switch (type) {
    case TYPE_RHYTHM_EVENT_ENUM.ARTIFACT:
      displayData.push(
        {
          title: 'Start time',
          value: convertToMomentJs(start || startTimeEpoch, timezoneOffset)?.format(
            'MM/DD/YYYY, HH:mm:ss',
          ),
        },
        {
          title: 'End time',
          value: convertToMomentJs(stop, timezoneOffset)?.format('MM/DD/YYYY, HH:mm:ss'),
        },
        {
          title: 'Duration',
          value: getTotalTimeWithin(ecgDataMap, start, stop),
        },
      );
      break;
    case TYPE_RHYTHM_EVENT_ENUM.AFIB:
    case TYPE_RHYTHM_EVENT_ENUM.TACHY:
    case TYPE_RHYTHM_EVENT_ENUM.BRADY:
    case TYPE_RHYTHM_EVENT_ENUM.AVB2:
    case TYPE_RHYTHM_EVENT_ENUM.AVB3:
      displayData.push(
        {
          title: 'Start time',
          value: convertToMomentJs(start || startTimeEpoch, timezoneOffset)?.format(
            'MM/DD/YYYY, HH:mm:ss',
          ),
        },
        {
          title: 'End time',
          value: convertToMomentJs(stop, timezoneOffset)?.format('MM/DD/YYYY, HH:mm:ss'),
        },
        {
          title: 'Duration',
          value:
            typesPersistEventV2.includes(type) || typesPersistEventV2.includes(oldType)
              ? getTotalTimeWithin(ecgDataMap, start, stop)
              : duration || getEventDuration(start, stop),
        },
        {
          title: 'Channel',
          value: !_.isNil(event?.channel || aiChannel) ? `CH${event?.channel || aiChannel}` : 'NA',
        },
        type === TYPE_RHYTHM_EVENT_ENUM.AFIB
          ? {
              title: 'Avg. HR',
              value: event?.avgHr > 0 ? `${event?.avgHr} bpm` : 'NA',
            }
          : {
              title: 'Heart rate',
              value: {
                min: event?.minHr > 0 ? `${event?.minHr} bpm` : 'NA',
                avg: event?.avgHr > 0 ? `${event?.avgHr} bpm` : 'NA',
                max: event?.maxHr > 0 ? `${event?.maxHr} bpm` : 'NA',
              },
            },
      );
      break;
    case TYPE_RHYTHM_EVENT_ENUM.PAUSE:
      displayData.push(
        {
          title: 'Start time',
          value: convertToMomentJs(start || startTimeEpoch, timezoneOffset)?.format(
            'MM/DD/YYYY, HH:mm:ss',
          ),
        },
        {
          title: 'Duration',
          value: duration || getEventDuration(start, stop),
        },
        {
          title: 'Channel',
          value: !_.isNil(event?.channel || aiChannel) ? `CH${event?.channel || aiChannel}` : 'NA',
        },
      );
      break;
    case TYPE_RHYTHM_EVENT_ENUM.VT:
    case TYPE_RHYTHM_EVENT_ENUM.SVT:
      displayData.push(
        {
          title: 'Start time',
          value: convertToMomentJs(start || startTimeEpoch, timezoneOffset)?.format(
            'MM/DD/YYYY, HH:mm:ss',
          ),
        },
        {
          title: 'End time',
          value: convertToMomentJs(stop, timezoneOffset)?.format('MM/DD/YYYY, HH:mm:ss'),
        },
        {
          title: 'No. beat',
          value: event?.countBeats
            ? `${event?.countBeats} beat${event?.countBeats > 1 ? 's' : ''}`
            : 'NA',
        },
        {
          title: 'Duration',
          value: duration || getEventDuration(start, stop),
        },
        {
          title: 'Channel',
          value: !_.isNil(event?.channel || aiChannel) ? `CH${event?.channel || aiChannel}` : 'NA',
        },
        {
          title: 'Heart rate',
          value: {
            min: event?.minHr > 0 ? `${event?.minHr} bpm` : 'NA',
            avg: event?.avgHr > 0 ? `${event?.avgHr} bpm` : 'NA',
            max: event?.maxHr > 0 ? `${event?.maxHr} bpm` : 'NA',
          },
        },
      );
      break;
    case TYPE_RHYTHM_EVENT_ENUM.MIN_HR:
      displayData.push(
        {
          title: 'Time',
          value: convertToMomentJs(start || startTimeEpoch, timezoneOffset)?.format(
            'MM/DD/YYYY, HH:mm:ss',
          ),
        },
        {
          title: 'Min HR',
          value: event?.minHr > 0 ? `${event?.minHr} bpm` : 'NA',
        },
      );
      break;
    case TYPE_RHYTHM_EVENT_ENUM.MAX_HR:
      displayData.push(
        {
          title: 'Time',
          value: convertToMomentJs(start || startTimeEpoch, timezoneOffset)?.format(
            'MM/DD/YYYY, HH:mm:ss',
          ),
        },
        {
          title: 'Max HR',
          value: event?.maxHr > 0 ? `${event?.maxHr} bpm` : 'NA',
        },
      );
      break;
    default:
  }
  if (BEAT_EVENT_LIST.includes(type)) {
    displayData.push({
      title: 'Start time',
      value: convertToMomentJs(start || startTimeEpoch, timezoneOffset)?.format(
        'MM/DD/YYYY, HH:mm:ss',
      ),
    });
    if (type === TYPE_BEAT_EVENT_ENUM.SVE_RUN || type === TYPE_BEAT_EVENT_ENUM.VE_RUN) {
      displayData.push(
        {
          title: 'End time',
          value: convertToMomentJs(stop, timezoneOffset)?.format('MM/DD/YYYY, HH:mm:ss'),
        },
        {
          title: 'No. beat',
          value: event?.countBeats
            ? `${event?.countBeats} beat${event?.countBeats > 1 ? 's' : ''}`
            : 'NA',
        },
      );
    }
    displayData.push({
      title: 'Channel',
      value: !_.isNil(event?.channel || aiChannel) ? `CH${event?.channel || aiChannel}` : 'NA',
    });
    if (type === TYPE_BEAT_EVENT_ENUM.SVE_RUN || type === TYPE_BEAT_EVENT_ENUM.VE_RUN) {
      displayData.push({
        title: 'Heart rate',
        value: {
          min: event?.minHr > 0 ? `${event?.minHr} bpm` : 'NA',
          avg: event?.avgHr > 0 ? `${event?.avgHr} bpm` : 'NA',
          max: event?.maxHr > 0 ? `${event?.maxHr} bpm` : 'NA',
        },
      });
    }
  }

  if (type && !_.isEmpty(captures) && type !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
    const capturesData = _.map(captures, (capture) => {
      const sampleMarkDraw = _.find(capture?.draw, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
      return {
        ...capture,
        startTime: sampleMarkDraw?.startTime || capture?.start,
        stopTime: sampleMarkDraw?.stopTime || capture?.stop,
        display: `${convertToMomentJs(sampleMarkDraw?.startTime || capture?.start, timezoneOffset)?.format('MM/DD/YYYY, HH:mm:ss')}`,
      };
    });
    displayData.push({
      title: 'Captured strips',
      type: 'ARRAY',
      value: _.sortBy(capturesData, 'startTime'),
    });
  }

  if (!type) {
    // minute info
    const { eventOthers, minuteInfoEvs } = info || {};

    displayData.push(
      {
        title: 'Time',
        value: convertToMomentJs(start || startTimeEpoch, timezoneOffset)?.format(
          'MM/DD/YYYY, HH:mm:ss',
        ),
      },
      {
        title: 'Channel',
        value: !_.isNil(event?.channel || aiChannel) ? `CH${event?.channel || aiChannel}` : 'NA',
      },
      {
        title: 'Heart rate',
        value: {
          min: event?.minHr > 0 ? `${event?.minHr} bpm` : 'NA',
          avg: event?.avgHr > 0 ? `${event?.avgHr} bpm` : 'NA',
          max: event?.maxHr > 0 ? `${event?.maxHr} bpm` : 'NA',
        },
      },
    );
    const minuteInfoEvents = _.uniqBy([...(eventOthers || []), ...(minuteInfoEvs || [])], 'id');
    const hasIdEvent = _.uniqBy(
      _.filter(minuteInfoEvents, (x) => x.idEvent),
      'idEvent',
    );
    const dontHasIdEvent = _.filter(minuteInfoEvents, (x) => !x.idEvent);
    const filterEventOthers = [...(hasIdEvent || []), ...(dontHasIdEvent || [])].filter(
      (x) =>
        !x.isRemovedStrip &&
        moment(x?.start).isBetween(
          moment(startTimeEpoch),
          moment(startTimeEpoch).add(1, 'minute'),
          undefined,
          '[]',
        ),
    );
    if (!_.isEmpty(filterEventOthers)) {
      const captures: any[] = [];
      _.forEach(filterEventOthers, (event: any) => {
        if (!_.isEmpty(event?.captures)) {
          _.forEach(event?.captures, (capture: any) => {
            const sampleMarkDraw = _.find(
              capture?.draw,
              (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK,
            );
            captures.push({
              ...event,
              ...capture,
              startTime: sampleMarkDraw?.startTime || capture?.start,
              stopTime: sampleMarkDraw?.stopTime || capture?.stop,
              display: `${convertToMomentJs(sampleMarkDraw?.startTime || capture?.start, timezoneOffset)?.format('MM/DD/YYYY, HH:mm:ss')}`,
            });
          });
        }
      });
      if (!_.isEmpty(captures)) {
        displayData.push({
          title: 'Captured strips',
          type: 'ARRAY',
          value: _.sortBy(captures, 'startTime'),
        });
      }
    }
  }
  return displayData;
};

/**
 * Builds the label shown for an ECG event in the strip list, numbering repeats of the same type. Resolves the type through oldType for deleted events, maps it to a display name or falls back to Minute info, and appends the event's one-based position among same-type siblings when more than one exists.
 *
 * @param event - The event being labelled.
 * @param data - The sibling events the ordinal is counted against.
 * @returns The display title, suffixed with an ordinal when the type repeats.
 */
export const formatEventTitle = (event: any, data: any[]) => {
  const countSameType = data.filter(
    (x) => x.type === event.type || x.oldType === event.oldType,
  ).length;
  const type =
    event?.type === TYPE_BEAT_ENUM.DELETED ? event?.oldType : event?.type || event?.oldType;
  const title = type ? (convertEventNameFromType as any)[type] : 'Minute info';
  if (countSameType > 1) {
    const orderOfSameType = data
      .filter((x) => x.type === event.type || x.oldType === event.oldType)
      .findIndex((x) => x.idEvent === event.idEvent);
    return `${title} ${orderOfSameType + 1}`;
  }
  return title;
};

/**
 * Determines whether an edited event has drifted back to its original state, ignoring purely presentational fields. Walks the original's keys with a deep comparison and bails out on the first difference, skipping stripImgs, technicianComments and title, and treats an empty side as unequal.
 *
 * @param original - The event as first loaded.
 * @param newData - The event carrying local edits.
 * @returns True when every compared field still matches the original.
 */
const compareSameEvent = (original: any, newData: any) => {
  if (_.isEmpty(original) || _.isEmpty(newData)) return false;
  let isEqual = true;
  _.forEach(Object.keys(original), (key) => {
    if (
      !_.isEqual(original[key], newData[key]) &&
      key !== 'stripImgs' &&
      key !== 'technicianComments' &&
      key !== 'title'
    ) {
      isEqual = false;
      return false;
    }
    return true;
  });
  return isEqual;
};

/**
 * Records the removal of a captured strip from an event in the pending event-change list. Acts only when the config asks for a removal, dropping the capture and rebuilding the flattened draw annotations, flagging isUpdateImage and appending the capture to removedCaptures unless it was never saved, dropping the entry entirely when the event has returned to its original state, and pushing a fresh entry when the event was not already tracked.
 *
 * @param events - The pending event changes, cloned rather than mutated.
 * @param updatedEvent - The event and the captureId or stripId being removed from it.
 * @param config - Carries isRemove, the only branch currently handled.
 * @returns The cloned event list, deduplicated by idEvent.
 */
export const updateListEventRemovedStrip = (
  events: any[] = [],
  updatedEvent: any = {},
  config: any = {},
) => {
  const { isRemove } = config || {};
  const cloneEvents = _.cloneDeep(events);
  const event = _.find(cloneEvents, (x) => x.idEvent === updatedEvent?.idEvent);
  if (event) {
    const { originEvent } = event || {};
    if (isRemove) {
      const { captureId, stripId } = updatedEvent || {};
      const { captures } = updatedEvent || {};
      const newCapture: any[] = _.cloneDeep(captures);
      const removedCapture: any = _.find(
        updatedEvent?.captures,
        (x: any) => x.captureId === captureId || x.stripId === stripId,
      );
      _.remove(newCapture, (x: any) => x.captureId === captureId);
      _.assign(event, {
        captures: newCapture,
        draw: _.flatten(_.map(newCapture, (x: any) => x.draw)),
      });
      if (!removedCapture.isNewCapture) {
        _.assign(event, {
          isUpdateImage: true,
          removedCaptures: _.uniqBy(
            [...(event.removedCaptures || []), { ...removedCapture, action: 'delete' }],
            'captureId',
          ),
        });
      }
      const isEqualOrigin = compareSameEvent(originEvent, event);
      if (isEqualOrigin) {
        _.remove(cloneEvents, (x) => x.idEvent === updatedEvent?.idEvent);
      }
      return _.uniqBy(cloneEvents, 'idEvent');
    }
  } else if (isRemove) {
    const { captureId, stripId } = updatedEvent || {};
    const { captures } = updatedEvent || {};
    const newCapture: any[] = _.cloneDeep(captures);
    const removedCapture: any = _.find(
      updatedEvent?.captures,
      (x: any) => x.captureId === captureId,
    );
    _.remove(newCapture, (x: any) => x.captureId === captureId || x.stripId === stripId);
    cloneEvents.push({
      ...updatedEvent.event,
      captures: newCapture,
      draw: _.flatten(_.map(newCapture, (x: any) => x.draw)),
      isUpdateImage: true,
      removedCaptures: !removedCapture.isNewCapture
        ? _.uniqBy(
            [...(updatedEvent.removedCaptures || []), { ...removedCapture, action: 'delete' }],
            'captureId',
          )
        : _.uniqBy([...(updatedEvent.removedCaptures || [])], 'captureId'),
    });
    return _.uniqBy(cloneEvents, 'idEvent');
  }

  return cloneEvents;
};
